import "@/App.css";
import { useAuth0 } from "@/Contexts";
import { useApiStore } from "@/States/api";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { requestSanity } from "@/Utils/api/sanity/request";
import {
  FetchedDownstreamHandlingTypes,
  FetchedHelpSections,
  FetchedLanguages,
  FetchedManufacturersTypes,
  FetchedOtherContaminants,
  FetchedTenantTypes,
  FetchedUpgradeTypes,
  FetchedWasteTypeCategories,
  FetchedWasteTypeClassificationSystems,
  FetchedWasteTypes,
  SmartIntegrations,
} from "@/Utils/api/sanity/types";
import { setMultiTenantHeaders } from "@/Utils/headerUtils";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { Spinner } from "@/components";
import { MultiSelectProvider } from "@/components/pageCards/multiListTable/multiSelectContext";
import SitePicker from "@/pages/sites";
import { useTrans } from "@/translations";

import { useEffect, useMemo } from "react";
import { BrowserRouter as Router } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { orderBy } from "lodash";
import "moment/dist/locale/nb";
import SuperJSON from "superjson";

import { isLocalhost } from "./Configs/config";
import { isMultiTenant } from "./Configs/config";
import Main from "./Main";
import { trpc } from "./Utils/trpc";
import { GlobalAlert } from "./components/AlertLine/GlobalAlert";
import { ContextMenu } from "./components/ContextMenu";
import { ConfirmationDialog } from "./components/Overlays/ConfirmationDialog";

export const Loading = () => {
  const { t, isTranslationLoaded } = useTrans();
  return (
    <div id="initapp">
      <Spinner />
      <h1>{isTranslationLoaded && t("hints:waitingForLoginServer")}</h1>
    </div>
  );
};

const reactQueryClientBase = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0, // No query retries
      staleTime: 1000 * 60 * 60, // 1 hour cache
      gcTime: 1000 * 60 * 60, // 1 hour cache
    },
  },
});

const App = () => {
  const { loading, isAuthenticated, loginWithRedirect, getTokenSilently } =
    useAuth0()!;

  const { language, isTranslationLoaded } = useTrans();
  const { reactQueryClient, setReactQueryClient } = useApiStore();
  const {
    setWasteTypes,
    setUpgrades,
    setDownstreamHandling,
    setTenantCategories,
    setManufacturers,
    setLanguages,
    setSmartIntegrations,
    setWasteTypeClassificationSystems,
    setHelpSections,
    setWasteTypeCategories,
    setOtherContaminants,
    languages,
  } = useCommonEntitiesStore();
  const { selectedSite } = useSelectedSite();

  useEffect(() => {
    setReactQueryClient(reactQueryClientBase);
  }, [setReactQueryClient]);

  const showSitePicker = useMemo(
    () => !selectedSite?.siteName && isMultiTenant,
    [selectedSite],
  );

  const serverPort = import.meta.env.VITE_SERVER_PORT ?? "4000";

  const trpcClient = useMemo(
    () =>
      trpc.createClient({
        links: [
          httpBatchLink({
            url: isLocalhost
              ? `http://localhost:${serverPort}/api/trpc`
              : "/api/trpc",
            transformer: SuperJSON,
            async headers() {
              const headers: { [key: string]: string | undefined } = {
                language,
                authorization: await getTokenSilently(),
              };
              setMultiTenantHeaders(selectedSite, headers);

              return headers;
            },
          }),
        ],
      }),
    [selectedSite, language, serverPort, getTokenSilently],
  );

  useEffect(() => {
    if (reactQueryClient) {
      requestSanity({ query: "languages" })
        .then((data) => setLanguages(orderBy(data as FetchedLanguages, "code")))
        .catch(console.error);
    }
  }, [setLanguages, reactQueryClient]);

  // Fetch and store container types
  useEffect(() => {
    if (reactQueryClient && languages) {
      requestSanity({ query: "wasteTypes", locale: language })
        .then((data) => setWasteTypes(data as FetchedWasteTypes))
        .catch(console.error);
      requestSanity({ query: "upgradesTypes", locale: language })
        .then((data) => setUpgrades(data as FetchedUpgradeTypes))
        .catch(console.error);
      requestSanity({ query: "downstreamHandlingTypes", locale: language })
        .then((data) =>
          setDownstreamHandling(data as FetchedDownstreamHandlingTypes),
        )
        .catch(console.error);
      requestSanity({ query: "tenantTypes", locale: language })
        .then((data) => setTenantCategories(data as FetchedTenantTypes))
        .catch(console.error);
      requestSanity({ query: "manufacturers", locale: language })
        .then((data) => setManufacturers(data as FetchedManufacturersTypes))
        .catch(console.error);
      requestSanity({ query: "smartIntegrations", locale: language })
        .then((data) => setSmartIntegrations(data as SmartIntegrations))
        .catch(console.error);
      requestSanity({
        query: "wasteTypeClassificationSystems",
        locale: language,
      })
        .then((data) =>
          setWasteTypeClassificationSystems(
            data as FetchedWasteTypeClassificationSystems,
          ),
        )
        .catch(console.error);
      requestSanity({ query: "helpSections", locale: language })
        .then((data) => setHelpSections(data as FetchedHelpSections))
        .catch(console.error);
      requestSanity({ query: "wasteTypeCategories", locale: language })
        .then((data) =>
          setWasteTypeCategories(data as FetchedWasteTypeCategories),
        )
        .catch(console.error);
      requestSanity({ query: "otherContaminants", locale: language })
        .then((data) => setOtherContaminants(data as FetchedOtherContaminants))
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactQueryClient, setWasteTypes, language, languages]);

  // useEffect(() => {
  //   if (isStaging) {
  //     // Set up HubSpot chat
  //     const script = document.createElement("script");
  //     script.src = "//js-na1.hs-scripts.com/19896481.js";
  //     script.async = true;
  //     script.defer = true;
  //     script.id = "hs-script-loader";
  //     document.body.appendChild(script);
  //   }
  // }, []);

  const clientsInitialized = useMemo(
    () => Boolean(trpcClient && reactQueryClient && isTranslationLoaded),
    [reactQueryClient, trpcClient, isTranslationLoaded],
  );

  if (loading) {
    console.log("Waiting for auth0 to initialize");
    return <Loading />;
  }

  if (!isAuthenticated) {
    console.log("Is not authenticated, need to log in");
    loginWithRedirect({
      appState: {
        targetUrl: `${window.location.pathname}${window.location.search}`,
      },
    });
    return <></>;
  }

  if (!clientsInitialized) {
    console.log("Waiting for clients to initialize");
    console.log(`TRPC client: ${!!trpcClient}`);
    console.log(`React Query client: ${!!reactQueryClient}`);
    return <Loading />;
  }

  return (
    <trpc.Provider client={trpcClient} queryClient={reactQueryClientBase}>
      <QueryClientProvider client={reactQueryClientBase}>
        <Router>
          <MultiSelectProvider>
            {showSitePicker ? <SitePicker /> : <Main />}
          </MultiSelectProvider>
          <ConfirmationDialog />
          <ContextMenu />
          <GlobalAlert />
        </Router>
      </QueryClientProvider>
    </trpc.Provider>
  );
};

export default App;
